import {
  authenticatedFastApiAxiosInstance,
  publicFastApiAxiosInstance,
} from "@/lib/data/http";
import {
  widgetReadSchema,
  widgetToolResultSchema,
  readWidgetPackage,
} from "@/lib/data/schemas/widgets";
export function widgetProxyUrl() {
  const baseUrl = publicFastApiAxiosInstance.defaults.baseURL;
  if (!baseUrl) throw new Error("The widget service is not configured.");
  return new URL(
    "widget-runtime/v1/proxy.html",
    baseUrl.endsWith("/") ? baseUrl : baseUrl + "/",
  ).href;
}
export async function getWidget(id: string, signal: AbortSignal) {
  const response = await authenticatedFastApiAxiosInstance.get(
    `widgets/v1/${encodeURIComponent(id)}`,
    { signal },
  );
  const value = widgetReadSchema.parse(response.data);
  const digest = Array.from(
    new Uint8Array(
      await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(value.html),
      ),
    ),
    (n) => n.toString(16).padStart(2, "0"),
  ).join("");
  if (digest !== value.sha256 || value.widgetId !== id)
    throw new Error("This widget failed its integrity check.");
  return readWidgetPackage(value.html);
}
export async function getWidgetBootstrap(signal: AbortSignal) {
  const response = await publicFastApiAxiosInstance.get<string>(
    "widget-runtime/v1/bootstrap.html",
    { signal, responseType: "text" },
  );
  if (typeof response.data !== "string" || response.data.length > 1_000_000)
    throw new Error("This widget requires a page reload.");
  return response.data;
}
export async function callWidgetTool(
  name: string,
  args: Record<string, unknown>,
  signal: AbortSignal,
) {
  try {
    const response = await authenticatedFastApiAxiosInstance.post(
      "widgets/v2/tools/call",
      { name, arguments: args },
      { signal, timeout: 35000 },
    );
    return widgetToolResultSchema.parse(response.data);
  } catch (error) {
    if (error && typeof error === "object" && "response" in error) {
      const response = error.response as { status?: number } | undefined;
      if (response && [401, 403, 404].includes(response.status ?? 0))
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: "Your access changed. Reload the page.",
            },
          ],
          _meta: { code: "access_changed" },
        };
    }
    throw new Error("The report could not load. Try Refresh again.");
  }
}
